import { action, computed, observable } from 'mobx';
import { isBrowserSupported } from 'braintree-web/venmo/shared/supports-venmo';
import Status from '../../constants/Status';
import { IPaymentMethodViewRegistry, IPaymentMethodViewProps } from '../../application/payment-setup/PaymentMethodViewRegistry';
import { paymentMethodsSetup } from '../../application/payment-setup/PaymentServiceSetup';
import { PaymentRepository } from '../../repositories/PaymentRepository/PaymentRepository';
import PAYMENT_PROVIDERS, { PAYMENT_MODES } from '../../constants/PaymentProviders';
import PaymentService, { IPaymentStrategiesMap } from '../../application/services/PaymentService';
import Payment, { PaymentProvider } from '../../domain/entities/Payment/Payment';
import { paymentsComparableHandler } from '../../application/payment-setup/PaymentMethodsOrder';
import { getPaymentDescriptorId } from '../../payment-strategies/PaymentDescriptor';
import { IPaymentStrategy } from '../../payment-strategies/PaymentStrategyBase';
import SavePrivacyPolicyUseCase from '../../use-cases/SavePrivacyPolicyUseCase';
import Config from '../../domain/entities/Config';

interface IPaymentMethodViewControllerData {
  status: Status;
  paymentProviders: PaymentProvider[];
  payment: Payment;
  selectedPaymentDescriptorId: string;
  isPaymentLoading: boolean;
  paymentStrategies: IPaymentStrategiesMap;
  isSingleMethodAvailable: boolean;
  isPayingInProgress: boolean;
  walletPaymentProviders: PaymentProvider[];
}

type RegistryComponent = React.ComponentType<IPaymentMethodViewProps>;

interface IPaymentMethodViewController {
  config: Config;
  paymentRepository: PaymentRepository;
  paymentMethodViewRegistry: IPaymentMethodViewRegistry;
  paymentMethodTitleRegistry: IPaymentMethodViewRegistry;
  paymentMethodIconRegistry: IPaymentMethodViewRegistry;
  paymentDisableMessageRegistry: IPaymentMethodViewRegistry;
  paymentService: PaymentService;
}

export class PaymentMethodViewController {
  private config: Config;

  private paymentRepository: PaymentRepository;

  private paymentMethodViewRegistry: IPaymentMethodViewRegistry;

  private paymentMethodTitleRegistry: IPaymentMethodViewRegistry;

  private paymentMethodIconRegistry: IPaymentMethodViewRegistry;

  private paymentDisableMessageRegistry: IPaymentMethodViewRegistry;

  private paymentService: PaymentService;

  @observable
  private status: Status = Status.IDLE;

  constructor(props: IPaymentMethodViewController) {
    this.config = props.config;
    this.paymentRepository = props.paymentRepository;
    this.paymentMethodViewRegistry = props.paymentMethodViewRegistry;
    this.paymentMethodTitleRegistry = props.paymentMethodTitleRegistry;
    this.paymentMethodIconRegistry = props.paymentMethodIconRegistry;
    this.paymentDisableMessageRegistry = props.paymentDisableMessageRegistry;
    this.paymentService = props.paymentService;
  }

  @action
  async load(): Promise<void> {
    try {
      this.setStatus(Status.LOADING);
      await this.paymentRepository.initialize();
      this.setStatus(Status.LOADED);
    } catch (err) {
      this.setStatus(Status.ERROR);
    }
  }

  @computed get data(): IPaymentMethodViewControllerData {
    return {
      isPayingInProgress: this.paymentService?.isPayingInProgress,
      isPaymentLoading: this.isPaymentLoading,
      isSingleMethodAvailable: this.isSingleMethodAvailable,
      payment: this.paymentRepository?.payment,
      paymentProviders: this.availablePaymentProviders.concat(this.disabledPaymentProviders),
      paymentStrategies: this.paymentService?.paymentStrategies,
      selectedPaymentDescriptorId: this.selectedPaymentDescriptorId,
      status: this.status,
      walletPaymentProviders: this.walletPaymentProviders,
    };
  }

  @computed private get availablePaymentProviders(): PaymentProvider[] {
    const { paymentProviders, areGiftCardsApplied } = this.paymentRepository.payment;
    const providers = paymentProviders || [];
    const filteredProviders = providers.filter(this.filterAvailableMethods(areGiftCardsApplied))
      .reduce((acc: PaymentProvider[], provider) => (
        paymentMethodsSetup[provider.providerName] ? [...acc, provider] : acc), []);

    return filteredProviders.sort(paymentsComparableHandler);
  }

  @computed private get walletPaymentProviders(): PaymentProvider[] {
    const { paymentProviders } = this.paymentRepository.payment;
    const providers = paymentProviders || [];
    const filteredProviders = providers.filter(provider => (provider.paymentMode === 'AWCApplePay' && this.isApplePayAvailable(provider.paymentMode)) ||
      provider.paymentMode === 'AWCGooglepay',
    ).reduce((acc: PaymentProvider[], provider) => (
      paymentMethodsSetup[provider.providerName] ? [...acc, provider] : acc), []);
    const providersWithHideTerms = filteredProviders.map(provider => {
      const hideTermsAndConditions = true;
      return {
        ...provider,
        hideTermsAndConditions: hideTermsAndConditions,
      };
    });
    return providersWithHideTerms.sort(paymentsComparableHandler);
  }

  @computed private get disabledPaymentProviders(): PaymentProvider[] {
    const { paymentProviders } = this.paymentRepository.payment;
    const providers = paymentProviders || [];
    const filteredProviders = providers.filter(item => item.showAsDisabled &&
      !this.nonDisplayableInPaymentMethodList.includes(item.providerName))
      .reduce((acc: PaymentProvider[], provider) => (
        paymentMethodsSetup[provider.providerName] ? [...acc, provider] : acc), []);

    return filteredProviders.sort(paymentsComparableHandler);
  }

  @computed private get nonDisplayableInPaymentMethodList(): string[] {
    return [
      PAYMENT_PROVIDERS.GIFT_CARD,
    ];
  }

  @computed private get selectedPaymentMethod(): PAYMENT_PROVIDERS {
    return this.paymentService?.paymentProvider;
  }

  @computed private get selectedPaymentMode(): PAYMENT_MODES {
    return this.paymentService?.paymentMode;
  }

  @computed private get paymentStrategy(): IPaymentStrategy {
    return this.paymentService?.paymentStrategy;
  }

  @computed private get isPaymentLoading(): boolean {
    if (this.paymentStrategy?.hasDisabledLoadingState) {
      return false;
    }

    const currentPaymentStatus = this.paymentService?.paymentStrategy?.status;
    return (currentPaymentStatus === Status.LOADING);
  }

  @computed private get selectedPaymentDescriptorId(): string {
    return getPaymentDescriptorId(this.selectedPaymentMethod, this.selectedPaymentMode);
  }

  @computed private get isSingleMethodAvailable(): boolean {
    // availablePaymentProviders getter can't be used in this scope because
    // some of the payment providers are not yet migrated
    const { paymentProviders, areGiftCardsApplied } = this.paymentRepository.payment;
    const providers = paymentProviders || [];
    const filteredProviders = providers.filter(this.filterAvailableMethods(areGiftCardsApplied));

    return filteredProviders.length === 1;
  }

  private filterAvailableMethods = (areGiftCardsApplied: boolean):
  (item: PaymentProvider) => boolean => (item: PaymentProvider) => item.isAvailable && (
    !areGiftCardsApplied || this.isAvailableWhileGiftCardApplied(
      item.providerName,
      item.paymentMode,
    )) && 
    this.isAfterpayEligible(item.providerName) &&
    this.isApplePayAvailable(item.paymentMode) &&
    this.checkVenmoBrowserSupport(item.paymentMode) &&
    !this.nonDisplayableInPaymentMethodList.includes(item.providerName);

  private isAfterpayEligible(providerName: string): boolean {
    const { hasInternationalBilling } = this.paymentRepository;

    return providerName !== PAYMENT_PROVIDERS.AFTERPAY || !hasInternationalBilling;
  }

  private isApplePayAvailable(paymentMode: string): boolean {
    if (paymentMode === PAYMENT_MODES.ADYEN_WEB_APPLEPAY) {
      return window.ApplePaySession ? true : false;
    }
    return true;
  }

  private checkVenmoBrowserSupport(paymentMode: string): boolean {
    if (paymentMode === PAYMENT_MODES.VENMO) {
      return isBrowserSupported({
        allowDesktop: true,
        allowNewBrowserTab: false,
      });
    }
    return true;
  }

  private isAvailableWhileGiftCardApplied(providerName: string, paymentMode: string): boolean {
    const { isGiftCardPaymentSupportedByMethodAndMode } = this.paymentRepository.payment;

    return isGiftCardPaymentSupportedByMethodAndMode(
      providerName,
      paymentMode,
    );
  }

  @action
  onPaymentSelect = (
    paymentProvider: PAYMENT_PROVIDERS,
    paymentMode: PAYMENT_MODES,
  ): void => {
    if (this.paymentService.isNewPayment(paymentProvider, paymentMode)) {
      this.paymentService.initPayment(paymentProvider, paymentMode);
    }
    if (!this.config?.messageOnlyTermsConditions) {
      const useCase = new SavePrivacyPolicyUseCase(false);
      useCase.execute();
    }
  };

  getPaymentMethodComponent = (
    id: string,
  ): RegistryComponent => this.paymentMethodViewRegistry.getComponentClass(id);

  getPaymentMethodTitleComponent = (
    id: string,
  ): RegistryComponent => this.paymentMethodTitleRegistry.getComponentClass(id);

  getPaymentMethodIconComponent = (
    id: string,
  ): RegistryComponent => this.paymentMethodIconRegistry.getComponentClass(id);

  getPaymentDisableMessageComponent = (
    id: string,
  ): RegistryComponent => this.paymentDisableMessageRegistry.getComponentClass(id);

  @action
  private setStatus(status: Status): void {
    this.status = status;
  }
}
